/**
 * Europe/London wall-clock helpers for the member app.
 *
 * Melio schedules in London time. Formatting a slot with `toLocaleTimeString`
 * and no `timeZone` renders it in the *viewer's* zone, so a member booking
 * from Madrid saw every slot an hour late, and grouping days with
 * `getFullYear()/getMonth()/getDate()` put late-evening slots in the wrong day
 * bucket. Both are fixed by naming the zone explicitly.
 *
 * This is the subset the booking flow needs. `apps/doctors/src/utils/londonTime.ts`
 * holds the fuller version (it also converts wall clock → instant for the
 * availability grid). The two apps are separate CRA builds with separate
 * node_modules and CRA's ModuleScopePlugin blocks cross-app imports, so this is
 * a deliberate mirror rather than a shared module. Keep them in step.
 */

const LONDON_DATE_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const LONDON_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hourCycle: 'h23',
  hour: '2-digit',
  minute: '2-digit',
});

const LONDON_LONG_DAY = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const LONDON_SHORT_DAY = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  weekday: 'short',
});

const LONDON_DAY_OF_MONTH = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: 'numeric',
});

const LONDON_SHORT_MONTH = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  month: 'short',
});

/** 'YYYY-MM-DD' in London — a stable key for grouping slots into days. */
export function londonDateKey(date: Date): string {
  const parts: Record<string, string> = {};
  for (const { type, value } of LONDON_DATE_PARTS.formatToParts(date)) {
    if (type !== 'literal') parts[type] = value;
  }
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** '11:30' in London. */
export function formatLondonTime(date: Date): string {
  return LONDON_TIME.format(date);
}

/** 'Tuesday 11 August' in London. */
export function formatLondonLongDay(date: Date): string {
  return LONDON_LONG_DAY.format(date);
}

/** 'Tue' in London. */
export function formatLondonWeekday(date: Date): string {
  return LONDON_SHORT_DAY.format(date);
}

/** '11' in London. */
export function formatLondonDayOfMonth(date: Date): string {
  return LONDON_DAY_OF_MONTH.format(date);
}

/** 'Aug' in London. */
export function formatLondonShortMonth(date: Date): string {
  return LONDON_SHORT_MONTH.format(date);
}
